import assignOffsetPagination from "@/schemas/assignOffsetPagination";
import { REWARDS_DISCOUNT_STATUS } from "@apps/rewards/constants/rewardsDiscountTypes";
import { RewardsDiscountCodeRewardSchema } from "@apps/rewards/schemas/RewardsRewardSchemas/RewardsDiscountRewardSchema";
import { RewardsFreeProductDiscountCodeRewardSchema } from "@apps/rewards/schemas/RewardsRewardSchemas/RewardsFreeProductRewardSchema";
import { RewardsGiftCardBalanceRewardSchema } from "@apps/rewards/schemas/RewardsRewardSchemas/RewardsGiftCardRewardSchema";
import { RewardsPointsRewardSchema } from "@apps/rewards/schemas/RewardsRewardSchemas/RewardsPointsRewardSchema";
import {
    assign,
    enums,
    literal,
    nullable,
    number,
    object,
    string,
    union,
} from "superstruct";

import { REWARDS_HISTORY_IMPORT_TYPES, REWARDS_HISTORY_TYPES } from "./types";

const RewardsHistoryActivitySchema = object({
    id: number(),
    title: string(),
});

const RewardsHistoryDiscountStatusSchema = object({
    discountStatus: enums(Object.values(REWARDS_DISCOUNT_STATUS)),
});

export const RewardsHistoryDiscountCodeRewardSchema = assign(
    RewardsDiscountCodeRewardSchema,
    RewardsHistoryDiscountStatusSchema
);

export const RewardsHistoryFreeProductDiscountCodeRewardSchema = assign(
    RewardsFreeProductDiscountCodeRewardSchema,
    RewardsHistoryDiscountStatusSchema
);

const RewardsHistoryRewardSchema = union([
    RewardsPointsRewardSchema,
    RewardsHistoryDiscountCodeRewardSchema,
    RewardsGiftCardBalanceRewardSchema,
    RewardsHistoryFreeProductDiscountCodeRewardSchema,
]);

const RewardsHistoryBaseSchema = object({
    id: number(),
    createdAt: string(),
    reward: nullable(RewardsHistoryRewardSchema),
    note: nullable(string()),
});

const RewardsHistoryRewardTypeSchema = assign(
    RewardsHistoryBaseSchema,
    object({
        type: literal(REWARDS_HISTORY_TYPES.reward),
        earningRule: RewardsHistoryActivitySchema,
    })
);

const RewardsHistoryRedeemTypeSchema = assign(
    RewardsHistoryBaseSchema,
    object({
        type: literal(REWARDS_HISTORY_TYPES.redeem),
        spentPoints: number(),
        spendingRule: RewardsHistoryActivitySchema,
    })
);

const RewardsHistoryTierTypeSchema = assign(
    RewardsHistoryBaseSchema,
    object({
        type: literal(REWARDS_HISTORY_TYPES.tier),
        tier: RewardsHistoryActivitySchema,
    })
);

const RewardsHistoryReferredTypeSchema = assign(
    RewardsHistoryBaseSchema,
    object({
        type: literal(REWARDS_HISTORY_TYPES.referred),
    })
);

const RewardsHistoryReferrerTypeSchema = assign(
    RewardsHistoryBaseSchema,
    object({
        type: literal(REWARDS_HISTORY_TYPES.referrer),
    })
);

const RewardsHistoryManualTypeSchema = assign(
    RewardsHistoryBaseSchema,
    object({
        type: literal(REWARDS_HISTORY_TYPES.manual),
        points: number(),
    })
);

const RewardsHistoryPointsExpiredTypeSchema = assign(
    RewardsHistoryBaseSchema,
    object({
        type: literal(REWARDS_HISTORY_TYPES.expired),
        expiredPoints: number(),
    })
);

const RewardsHistoryImportTypeSchema = assign(
    RewardsHistoryBaseSchema,
    object({
        type: literal(REWARDS_HISTORY_TYPES.import),
        rewardProgram: enums(Object.values(REWARDS_HISTORY_IMPORT_TYPES)),
        tier: nullable(RewardsHistoryActivitySchema),
    })
);

export const RewardsHistoryRefundTypeSchema = assign(
    RewardsHistoryBaseSchema,
    object({
        type: literal(REWARDS_HISTORY_TYPES.refund),
        refundedActivity: union([
            RewardsHistoryRewardTypeSchema,
            RewardsHistoryRedeemTypeSchema,
            RewardsHistoryTierTypeSchema,
            RewardsHistoryReferredTypeSchema,
            RewardsHistoryReferrerTypeSchema,
            RewardsHistoryImportTypeSchema,
        ]),
        refundedPoints: nullable(number()),
    })
);

export const RewardsHistorySchema = union([
    RewardsHistoryRewardTypeSchema,
    RewardsHistoryTierTypeSchema,
    RewardsHistoryRedeemTypeSchema,
    RewardsHistoryReferredTypeSchema,
    RewardsHistoryReferrerTypeSchema,
    RewardsHistoryManualTypeSchema,
    RewardsHistoryPointsExpiredTypeSchema,
    RewardsHistoryImportTypeSchema,
    RewardsHistoryRefundTypeSchema,
]);

export const RewardsHistoryResponseSchema =
    assignOffsetPagination(RewardsHistorySchema);
